import { getErrorLogContext, trackError as trackErrorUtil } from './error.utils';
import { logDebug as logDebugUtil, logInfo as logInfoUtil, logWarn as logWarnUtil, logError as logErrorUtil } from './logger.utils';
import { reportAction as reportActionUtil } from './action.utils';
/**
 * Aggregates common logging functionality and allows to provide common context props and a message prefix
 * (which will be passed to any of the logging methods).
 *
 * Use it as is, or inside your feature's context provider.
 *
 * @example
 * See OneHomeContext.provider.tsx
 */
export class LoggerService {
    constructor({ featureName, featureVersion = 1, commonContext = {}, messagePrefix, sampleRate = 1, enrichContext }) {
        this.featureName = featureName;
        this.featureVersion = featureVersion;
        this.commonContext = commonContext;
        this.messagePrefix = messagePrefix;
        this.sampleRate = sampleRate;
        this.enrichContext = enrichContext;
    }
    debug(message, context) {
        this.logDebug(message, context);
    }
    info(message, context) {
        this.logInfo(message, context);
    }
    warn(message, context) {
        this.logWarn(message, context);
    }
    error(error, context) {
        const message = typeof error === 'string' ? error : error.message;
        this.logError(message, { ...getErrorLogContext(error), ...context });
    }
    getContext(originalContext = {}) {
        const context = { ...this.commonContext, ...originalContext };
        if (this.featureName) {
            context[this.featureName] = this.featureVersion;
        }
        return Object.keys(context).length === 0 ? undefined : context;
    }
    getMessage(originalMessage) {
        if (!originalMessage) {
            return;
        }
        if (this.messagePrefix) {
            return `${this.messagePrefix} ${originalMessage}`;
        }
        return originalMessage;
    }
    /**
     * Get a new LoggerUtils (for a sub feature) which extends the current LoggerUtils
     */
    extend({ featureName, featureVersion = 1, commonContext = {}, messagePrefix, enrichContext }) {
        return new LoggerService({
            featureName: featureName,
            featureVersion: featureVersion,
            commonContext: { ...this.getContext(commonContext) },
            messagePrefix: this.messagePrefix
                ? `${this.messagePrefix} ${messagePrefix}`
                : messagePrefix,
            enrichContext: enrichContext || this.enrichContext
        });
    }
    /**
     * Tracks an Error (to DD RUM)
     */
    trackError(errorProps) {
        const message = this.getMessage(errorProps.message);
        trackErrorUtil({
            error: errorProps.error,
            ...(message && { message }),
            context: this.getContext(errorProps.context),
            enrichContext: this.enrichContext
        });
    }
    /**
     * Logs a debug message (to DD Logs)
     */
    logDebug(message, context = {}) {
        logDebugUtil(this.getMessage(message), this.getContext(context), this.enrichContext);
    }
    /**
     * Logs an info message (to DD Logs)
     */
    logInfo(message, context = {}, sampleRate) {
        logInfoUtil(this.getMessage(message), this.getContext(context), sampleRate || this.sampleRate, this.enrichContext);
    }
    /**
     * Logs a warn message (to DD Logs)
     */
    logWarn(message, context = {}, sampleRate) {
        logWarnUtil(this.getMessage(message), this.getContext(context), sampleRate || this.sampleRate, this.enrichContext);
    }
    /**
     * Logs an error message (to DD Logs)
     */
    logError(message, context = {}, sampleRate) {
        logErrorUtil(this.getMessage(message), this.getContext(context), sampleRate || this.sampleRate, this.enrichContext);
    }
    /**
     * Tracks a user action (to DD RUM)
     */
    trackUserAction(trackActionProps) {
        const { name, context = {} } = trackActionProps;
        reportActionUtil({
            name: name, // actionName should not be prefixed with messagePrefix
            context: this.getContext(context)
        }, this.enrichContext);
    }
}
//# sourceMappingURL=logger.service.js.map